import { Injectable, inject } from '@angular/core';
import { TuiDay, TuiTime } from '@taiga-ui/cdk';
import { DateTime } from 'luxon';
import { defer, interval, merge, Observable } from 'rxjs';
import { filter, map, share, startWith, tap } from 'rxjs/operators';

import {
  BrowserTabLifecycleService,
  BrowserTabState,
} from '@common/services/browser-tab-lifecycle.service';
import { transformLuxonDateToTuiDate } from '@common/utils/date-form-transform';
import { dateTimeWithUserTimezone } from '@common/utils/format-date';

/**
 * Сервис для отслеживания смены суток и оповещения подписчиков
 */
@Injectable({
  providedIn: 'root',
})
export class DateChangeService {
  private readonly browserTabLifecycleService = inject(BrowserTabLifecycleService);

  /** Текущая дата для сравнения */
  private currentDate: DateTime = dateTimeWithUserTimezone();

  /**
   * Общий поток смены суток. Проверка раз в минуту и при возврате на вкладку;
   * `share()` запускает её на первом подписчике и останавливает, когда отписался последний.
   */
  private readonly dateChange$: Observable<DateTime> = merge(
    interval(60000),
    // defer: onTabLifecycleChange() сам считает подписчиков, звать его нужно на каждой подписке
    defer(() => this.browserTabLifecycleService.onTabLifecycleChange()).pipe(
      filter((state) => state === BrowserTabState.ACTIVE),
    ),
  ).pipe(
    startWith(0),
    map(() => dateTimeWithUserTimezone()),
    filter((newDate) => !newDate.hasSame(this.currentDate, 'day')),
    tap((newDate) => (this.currentDate = newDate)),
    share(),
  );

  /**
   * Возвращает текущую дату и время в формате TuiDay с временем конца дня
   * @returns {TuiDay | [TuiDay | null, TuiTime | null] | null} Текущая дата и время в конце дня в часовом поясе пользователя
   */
  public getCurrentDateTime = (): TuiDay | [TuiDay | null, TuiTime | null] | null =>
    transformLuxonDateToTuiDate(dateTimeWithUserTimezone().endOf('day'), {
      withTime: true,
    }) as TuiDay | [TuiDay | null, TuiTime | null] | null;

  /**
   * Возвращает Observable для подписки на события смены суток
   * @returns {Observable<DateTime>} Поток дат при смене суток
   */
  public onDateChange(): Observable<DateTime> {
    return this.dateChange$;
  }
}
